import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Row, Col, ButtonGroup } from 'reactstrap';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { createSelector } from '@reduxjs/toolkit';
import TableContainerReactTable from '../../../../Components/Common/TableContainerReactTable';
import LoadingIndicator from '../../../../Components/Loading/LoadingIndicator';
import { useGetProductImageQuery } from '../../redux/productApiSlice';
import { ProductImageData } from '../../types/productImageTypes';
import { RowSelectionState } from '@tanstack/react-table';
import ImageViewModal from '../modals/ImageViewModal';
import { toast } from 'react-toastify';

// Create a memoized selector to prevent unnecessary re-renders
const selectSelectedProduct = createSelector(
  [(state: RootState) => state.products.selectedProduct],
  (selectedProduct) => selectedProduct
);

interface ImageTabProps {
  isActive?: boolean;
}

const ImageTab: React.FC<ImageTabProps> = ({ isActive = false }) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [imageData, setImageData] = useState<ProductImageData[]>([]);
  const [isImageViewModalOpen, setIsImageViewModalOpen] = useState(false);
  const selectedProduct = useSelector(selectSelectedProduct);

  // API query for product images
  const { 
    data: productImages, 
    isLoading: isLoadingProductImages, 
    isFetching: isFetchingProductImages, 
    error: productImagesError,
    refetch: refetchProductImages 
  } = useGetProductImageQuery(
    { productRowId: selectedProduct?.rowId || 0 },
    { skip: !isActive || !selectedProduct?.rowId }
  );

  // Separate loading states for initial load vs refetch
  const isInitialLoading = isLoadingProductImages;
  const isRefreshing = isFetchingProductImages && !isLoadingProductImages;

  // Note: API is automatically called when isActive becomes true due to skip option in useGetProductImageQuery

  // Clear row selection when product changes
  useEffect(() => {
    setRowSelection({});
  }, [selectedProduct?.rowId]);

  // Sync API data with local state
  useEffect(() => {
    if (productImages?.data) {
      setImageData(productImages.data);
    }
  }, [productImages]);

  // Handle API errors with toast notifications
  useEffect(() => {
    if (productImagesError) {
      console.error('Error fetching product images:', productImagesError);
      toast.error('Failed to load product images. Please try again.');
    }
  }, [productImagesError]);

  // Handle checkbox changes
  const handleCheckboxChange = useCallback((rowIndex: number, field: keyof ProductImageData, value: boolean) => {
    setImageData(prevData => 
      prevData.map((item, index) => 
        index === rowIndex ? { ...item, [field]: value } : item
      )
    );
  }, []);

  const handleNew = () => {
    console.log('Creating new image...');
    // Implement new image logic
  };

  const handleSave = () => {
    console.log('Saving image changes...', imageData);
    // TODO: Implement API call to save the updated image data
  };

  const handleCancel = () => {
    console.log('Cancelling changes...');
    // Reset to original API data and clear selection
    if (productImages?.data) {
      setImageData(productImages.data);
    }
    setRowSelection({});
  };

  const handleDelete = () => {
    console.log('Deleting image...');
    // Implement delete logic
  };

  const handleView = () => {
    const selectedRow = getSelectedRow();
    if (!selectedRow) {
      toast.error('Please select an image to view');
      return;
    }
    
    if (!selectedRow.image) {
      toast.error('No image file found for the selected row');
      return;
    }
    
    console.log('Viewing image:', selectedRow.image);
    setIsImageViewModalOpen(true);
  };

  const handleRefresh = () => {
    refetchProductImages();
  };

  const handleRowSelectionChange = useCallback((updaterOrValue: any) => {
    console.log('Row selection changed:', updaterOrValue);
    setRowSelection(updaterOrValue);
  }, []);

  const handleRowClick = useCallback((row: any) => {
    console.log('Row clicked:', row.original);
    // Single row selection - select only the clicked row
    const rowId = row.id;
    setRowSelection(prev => {
      // If the same row is clicked again, deselect it
      if (prev[rowId]) {
        return {};
      }
      // Otherwise, select only this row
      return { [rowId]: true };
    });
  }, []);

  // Helper function to get selected row (single selection)
  const getSelectedRow = useCallback((): ProductImageData | null => {
    const selectedRowIds = Object.keys(rowSelection).filter(key => rowSelection[key]);
    if (selectedRowIds.length === 0) return null;
    
    const selectedRowId = selectedRowIds[0]; // Only one row can be selected
    const foundRow = imageData.find((row: ProductImageData) => 
      row.rowId?.toString() === selectedRowId
    );
    return foundRow || null;
  }, [rowSelection, imageData]);

  // Check if a row is selected
  const hasSelectedRow = Object.keys(rowSelection).some(key => rowSelection[key]);

  // Check if there are pending modifications (simple comparison)
  const hasPendingChanges = JSON.stringify(imageData) !== JSON.stringify(productImages?.data || []);

  // Column definitions
  const columns = useMemo(
    () => [
      {
        header: "Image No",
        accessorKey: "imageNumber",
        enableColumnFilter: false,
        size: 80,
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        size: 150,
      },
      {
        header: "Created",
        accessorKey: "created",
        enableColumnFilter: false,
        size: 120,
        cell: (cell: any) => {
          const dateValue = cell.getValue();
          if (!dateValue) return null;
          // Format the date to a more readable format
          const date = new Date(dateValue);
          return date.toLocaleDateString();
        },
      },
      {
        header: "Created By",
        accessorKey: "createdName",
        enableColumnFilter: false,
        size: 120,
      },
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
        size: 100,
      },
      {
        header: "Primary Flag",
        accessorKey: "primary",
        enableColumnFilter: false,
        size: 100,
        cell: (cell: any) => {
          const rowIndex = cell.row.index;
          const isChecked = cell.getValue();
          return (
            <div className="text-center">
              <input
                type="checkbox"
                className="form-check-input"
                checked={isChecked || false}
                onChange={(e) => handleCheckboxChange(rowIndex, 'primary', e.target.checked)}
              />
            </div>
          );
        },
      },
      {
        header: "Active",
        accessorKey: "activeFlag",
        enableColumnFilter: false,
        size: 80,
        cell: (cell: any) => {
          const rowIndex = cell.row.index;
          const isChecked = cell.getValue();
          return (
            <div className="text-center">
              <input
                type="checkbox"
                className="form-check-input"
                checked={isChecked || false}
                onChange={(e) => handleCheckboxChange(rowIndex, 'activeFlag', e.target.checked)}
              />
            </div>
          );
        },
      },
      {
        header: "Order",
        accessorKey: "order",
        enableColumnFilter: false,
        size: 80,
      },
      {
        header: "Comments",
        accessorKey: "comments",
        enableColumnFilter: false,
        size: 200,
      },
    ],
    [handleCheckboxChange]
  );

  return (
    <div className="page-content p-0">
      <Row className="mb-3">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <ButtonGroup className='gap-2'>
              {/* Core Actions */}
              <button
                onClick={handleNew}
                className="d-inline-flex align-items-center btn btn-soft-primary btn-sm"
              >
                <i className="ri-add-line align-middle me-1"></i> New
              </button>
              <button
                onClick={handleSave}
                className={`d-inline-flex align-items-center btn btn-sm ${hasPendingChanges ? 'btn-soft-success' : 'btn-soft-secondary'}`}
                disabled={!hasPendingChanges}
              >
                <i className="ri-save-line align-middle me-1"></i> Save
              </button>
              <button
                onClick={handleCancel}
                className={`d-inline-flex align-items-center btn btn-sm ${hasPendingChanges ? 'btn-soft-warning' : 'btn-soft-secondary'}`}
                disabled={!hasPendingChanges}
              >
                <i className="ri-close-line align-middle me-1"></i> Cancel
              </button>
              <button
                onClick={handleDelete}
                className={`d-inline-flex align-items-center btn btn-sm ${hasSelectedRow ? 'btn-soft-danger' : 'btn-soft-secondary'}`}
                disabled={!hasSelectedRow}
              >
                <i className="ri-delete-bin-line align-middle me-1"></i> Delete
              </button>
              <button
                onClick={handleView}
                className={`d-inline-flex align-items-center btn btn-sm ${hasSelectedRow ? 'btn-soft-info' : 'btn-soft-secondary'}`}
                disabled={!hasSelectedRow}
              >
                <i className="ri-image-line align-middle me-1"></i> View
              </button>
              <button
                onClick={handleRefresh}
                className={`btn btn-outline-primary btn-sm ${isRefreshing ? 'btn-load' : ''}`}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <span className="d-flex align-items-center">
                    <span 
                      className="spinner-border flex-shrink-0" 
                      role="status"
                      style={{ width: '0.875rem', height: '0.875rem', borderWidth: '1px' }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </span>
                    <span className="ms-2">
                      Refreshing...
                    </span>
                  </span>
                ) : (
                  <>
                    <i className="ri-refresh-line align-middle me-1"></i> Refresh
                  </>
                )}
              </button>
            </ButtonGroup>
            <div className="d-flex align-items-center gap-3">
              {hasSelectedRow && (
                <div className="text-muted small">
                  <i className="ri-checkbox-line align-middle me-1"></i>
                  1 row selected
                </div>
              )}
              {hasPendingChanges && (
                <div className="text-warning small">
                  <i className="ri-edit-line align-middle me-1"></i>
                  Changes pending
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Image content */}
      <div className="image-content">
        {isInitialLoading ? (
          <LoadingIndicator
            isLoading={true}
            title="Loading product images..."
            height="300px"
          />
        ) : (
          <TableContainerReactTable
            columns={columns}
            data={imageData}
            divClass="table-responsive"
            tableClass="align-middle table-nowrap"
            theadClass="table-light"
            enableRowSelection={true}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            onRowClick={handleRowClick}
            getRowId={(row) => row.rowId?.toString()}
            enableMultiRowSelection={false}
            verticalLines={true}
          />
        )}
      </div>

      {/* Image View Modal */}
      <ImageViewModal
        isOpen={isImageViewModalOpen}
        onClose={() => setIsImageViewModalOpen(false)}
        imageData={getSelectedRow()}
      />
    </div>
  );
};

export default ImageTab;